import type { GameState } from '../engine';
import { uuidV4 } from '../utils/uuid';

export type UserProfile = {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  lastState?: GameState;
};

const PROFILES_KEY = 'omulongo_user_profiles_v1';
const ACTIVE_PROFILE_ID_KEY = 'omulongo_active_profile_id_v1';

function nowIso(): string {
  return new Date().toISOString();
}

function safeJsonParse<T>(value: string | null): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

function randomId(): string {
  return uuidV4();
}

function clampName(value: string): string {
  const trimmed = (value || '').trim();
  const name = trimmed || 'Player';
  return name.slice(0, 24);
}

function loadProfiles(): UserProfile[] {
  if (typeof localStorage === 'undefined') return [];
  const parsed = safeJsonParse<unknown>(localStorage.getItem(PROFILES_KEY));
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const obj: any = entry;
      if (typeof obj.id !== 'string' || typeof obj.name !== 'string') return null;
      return {
        id: obj.id,
        name: clampName(obj.name),
        createdAt: typeof obj.createdAt === 'string' ? obj.createdAt : nowIso(),
        updatedAt: typeof obj.updatedAt === 'string' ? obj.updatedAt : nowIso(),
        lastState: obj.lastState as GameState | undefined,
      } satisfies UserProfile;
    })
    .filter(Boolean) as UserProfile[];
}

function saveProfiles(profiles: UserProfile[]): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(PROFILES_KEY, JSON.stringify(profiles));
}

export function listUserProfiles(): UserProfile[] {
  return loadProfiles().sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export function getActiveUserProfileId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = (localStorage.getItem(ACTIVE_PROFILE_ID_KEY) || '').trim();
  return raw || null;
}

export function getActiveUserProfileName(): string | null {
  const profiles = loadProfiles();
  const activeId = getActiveUserProfileId();
  const active = activeId ? profiles.find((p) => p.id === activeId) : null;
  return active?.name ?? null;
}

export function setActiveUserProfileId(id: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(ACTIVE_PROFILE_ID_KEY, id);
}

export function ensureDefaultUserProfile(): UserProfile {
  const profiles = loadProfiles();
  let activeId = getActiveUserProfileId();
  let active = activeId ? profiles.find((p) => p.id === activeId) : null;

  if (!profiles.length) {
    const created: UserProfile = { id: randomId(), name: 'Player', createdAt: nowIso(), updatedAt: nowIso() };
    saveProfiles([created]);
    setActiveUserProfileId(created.id);
    return created;
  }

  if (!active) {
    active = profiles[0];
    setActiveUserProfileId(active.id);
  }

  return active;
}

export function createUserProfile(name: string): UserProfile {
  const profiles = loadProfiles();
  const created: UserProfile = { id: randomId(), name: clampName(name), createdAt: nowIso(), updatedAt: nowIso() };
  profiles.push(created);
  saveProfiles(profiles);
  setActiveUserProfileId(created.id);
  return created;
}

export function renameUserProfile(id: string, name: string): void {
  const profiles = loadProfiles();
  const idx = profiles.findIndex((p) => p.id === id);
  if (idx < 0) return;
  profiles[idx] = { ...profiles[idx], name: clampName(name), updatedAt: nowIso() };
  saveProfiles(profiles);
}

export function deleteUserProfile(id: string): void {
  const profiles = loadProfiles();
  const next = profiles.filter((p) => p.id !== id);
  saveProfiles(next);
  const activeId = getActiveUserProfileId();
  if (activeId === id) {
    setActiveUserProfileId(next[0]?.id || '');
  }
}

export function loadActiveProfileState(): GameState | null {
  const profiles = loadProfiles();
  const activeId = getActiveUserProfileId();
  const active = activeId ? profiles.find((p) => p.id === activeId) : null;
  return (active?.lastState as GameState | undefined) ?? null;
}

export function saveActiveProfileState(state: GameState): void {
  const profiles = loadProfiles();
  const activeId = getActiveUserProfileId();
  const active = activeId ? profiles.findIndex((p) => p.id === activeId) : -1;
  if (active < 0) {
    const created = ensureDefaultUserProfile();
    const after = loadProfiles();
    const idx = after.findIndex((p) => p.id === created.id);
    if (idx < 0) return;
    after[idx] = { ...after[idx], lastState: state, updatedAt: nowIso() };
    saveProfiles(after);
    return;
  }
  profiles[active] = { ...profiles[active], lastState: state, updatedAt: nowIso() };
  saveProfiles(profiles);
}

export function clearActiveProfileState(): void {
  const profiles = loadProfiles();
  const activeId = getActiveUserProfileId();
  const idx = activeId ? profiles.findIndex((p) => p.id === activeId) : -1;
  if (idx < 0) return;
  profiles[idx] = { ...profiles[idx], lastState: undefined, updatedAt: nowIso() };
  saveProfiles(profiles);
}
